import { ViewColumnsIcon } from "@heroicons/react/24/outline";
import { RegionId } from "@sp-crm/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ContentFull, ContentHeader, ContentSubheader } from "components/layout";
import {
    AddReferralPipelineSection,
    ReferralPipelineSettings,
} from "components/manage/agency-settings/agency-settings-referral-pipelines";
import { PipelineSummary } from "components/referral-pipelines/types";
import { trpc } from "components/rpc/trpc";
import { ErrorMessage } from "components/ui/error-message";
import { InternalLink } from "components/ui/internal-link";
import { Spinner } from "components/ui/spinner";
import { Toggle } from "components/ui/toggle";
import React, { useCallback } from "react";
import { useCurrentRegion, useRegionId } from "store/selectors/hooks";

export const RegionSettingsReferralPipelines: React.FC<unknown> = () => {
    const region = useCurrentRegion();
    const regionId = useRegionId();
    const trpcClient = trpc.useTRPC();
    const queryClient = useQueryClient();

    const pipelinesQuery = useQuery({
        ...trpcClient.pipeline.list.queryOptions({
            regionId: regionId ?? ("" as RegionId),
        }),
        enabled: !!regionId,
    });

    const refetchPipelines = useCallback(async () => {
        await queryClient.invalidateQueries({ queryKey: [["pipeline"]] });
    }, [queryClient]);

    if (!regionId) {
        return null;
    }
    if (pipelinesQuery.isPending) {
        return <Spinner />;
    }
    if (pipelinesQuery.isError) {
        return (
            <ErrorMessage component="RegionSettingsReferralPipelines">
                <pre>{JSON.stringify(pipelinesQuery.error, null, 2)}</pre>
            </ErrorMessage>
        );
    }

    const pipelines = pipelinesQuery.data ?? [];
    const agencyPipelines = pipelines.filter(pipeline => pipeline.scope === "agency");
    const officePipelines = pipelines.filter(pipeline => pipeline.scope === "office");

    return (
        <ContentFull>
            <ContentHeader>{`Referral pipelines - ${region?.name}`}</ContentHeader>
            <ContentSubheader>
                {`Pipelines are boards where referral contacts and referral organizations move through stages. Pipelines inherited from the agency are managed in the agency settings; pipelines added here only appear for ${region?.name}.`}
            </ContentSubheader>
            <div className="max-w-2xl space-y-4">
                {agencyPipelines.map(pipeline => (
                    <InheritedReferralPipeline key={pipeline.id} pipeline={pipeline} />
                ))}
                {officePipelines.map(pipeline => (
                    <ReferralPipelineSettings
                        key={pipeline.id}
                        pipeline={pipeline}
                        scope={{ kind: "office", regionId }}
                        onChanged={refetchPipelines}
                    />
                ))}
                <AddReferralPipelineSection
                    scope={{ kind: "office", regionId }}
                    label={`Add referral pipeline for ${region?.name}`}
                    onCreated={refetchPipelines}
                />
            </div>
        </ContentFull>
    );
};

interface InheritedReferralPipelineProps {
    pipeline: PipelineSummary;
}

const ignoreAutoAddChange = (): void => undefined;

const InheritedReferralPipeline: React.FC<InheritedReferralPipelineProps> = props => {
    const { pipeline } = props;
    return (
        <section className="rounded-lg border border-gray-200">
            <div className="flex items-center gap-2 rounded-t-lg border-b border-gray-200 bg-gray-50 px-3 py-2.5">
                <ViewColumnsIcon className="h-5 w-5 shrink-0 text-gray-600" />
                <h3 className="min-w-0 flex-1 truncate text-sm font-semibold text-gray-900">
                    {pipeline.name}
                </h3>
                <span className="inline-flex shrink-0 items-center rounded bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-600">
                    Inherited from agency
                </span>
                <InternalLink
                    to={`/pipelines/${pipeline.id}`}
                    className="shrink-0 text-sm">
                    View pipeline
                </InternalLink>
            </div>
            <div className="px-3">
                <ul className="divide-y divide-gray-200">
                    {pipeline.stages.length === 0 ? (
                        <li className="py-2 text-sm text-gray-500">No stages yet</li>
                    ) : (
                        pipeline.stages.map(stage => (
                            <li key={stage.id} className="py-2 text-sm text-gray-900">
                                {stage.name}
                            </li>
                        ))
                    )}
                </ul>
            </div>
            <div className="border-t border-gray-200 px-3 py-2.5">
                <Toggle
                    checked={pipeline.autoAddNewReferralSources}
                    disabled
                    onChange={ignoreAutoAddChange}
                    label="Automatically add new referral sources to this pipeline"
                />
            </div>
        </section>
    );
};
